import { CurrencyPipe } from '@angular/common';
import { StripeBillingInterval } from '../enums/stripe.enum';
import { StripeProduct } from '../interfaces/stripe';

const PERIOD_KEYS = {
  day: {
    one: 'company-tool.snippets.billing-period.per-day',
    many: 'company-tool.snippets.billing-period.every-n-days',
  },
  week: {
    one: 'company-tool.snippets.billing-period.per-week',
    many: 'company-tool.snippets.billing-period.every-n-weeks',
  },
  month: {
    one: 'company-tool.snippets.billing-period.per-month',
    many: 'company-tool.snippets.billing-period.every-n-months',
  },
  year: {
    one: 'company-tool.snippets.billing-period.per-year',
    many: 'company-tool.snippets.billing-period.every-n-years',
  },
} as const;

const PRICE_WITH_PERIOD_KEY =
  'company-tool.views.settings.company.payment.sections.general.price-with-period';

export type StripeRecurringInterval = 'day' | 'week' | 'month' | 'year';

/** Minimal Transloco surface used by billing-period helpers. */
export type BillingPeriodTranslator = {
  translate: (key: string, params?: Record<string, unknown>) => string;
  getActiveLang: () => string;
};

/**
 * Reads Stripe recurring interval_count from a catalog price.
 * @param {Pick<StripeProduct, 'recurring'> | null | undefined} product Catalog price
 * @returns {number} Interval count, defaults to 1
 */
export function stripeIntervalCount(
  product: Pick<StripeProduct, 'recurring'> | null | undefined,
): number {
  const count = product?.recurring?.interval_count;
  return typeof count === 'number' && count > 0 ? count : 1;
}

/**
 * Resolves billing cycle from a catalog price, falling back to the toggle.
 * @param {Pick<StripeProduct, 'recurring'> | null | undefined} product Catalog price
 * @param {StripeBillingInterval} billingInterval Selected billing toggle
 * @returns {{ interval: StripeRecurringInterval; count: number }}
 */
export function resolveBillingCycle(
  product: Pick<StripeProduct, 'recurring'> | null | undefined,
  billingInterval: StripeBillingInterval,
): { interval: StripeRecurringInterval; count: number } {
  if (product?.recurring?.interval) {
    return {
      interval: product.recurring.interval,
      count: stripeIntervalCount(product),
    };
  }
  return { interval: billingInterval, count: 1 };
}

/**
 * Resolves i18n key + params for a billing cycle label.
 * Count 1 → "jeden Monat" / "every month"; count > 1 → "alle N Monate" / "every N months".
 * @param {StripeRecurringInterval} interval Stripe recurring interval
 * @param {number} count Stripe interval_count
 * @returns {{ key: string; params: { count: number } }}
 */
export function cyclePeriodLabel(
  interval: StripeRecurringInterval,
  count: number,
): { key: string; params: { count: number } } {
  const keys = PERIOD_KEYS[interval];
  const safeCount = count > 0 ? count : 1;
  return {
    key: safeCount > 1 ? keys.many : keys.one,
    params: { count: safeCount },
  };
}

/**
 * Translates a billing cycle period label.
 * @param {BillingPeriodTranslator} i18n Transloco service
 * @param {StripeRecurringInterval} interval Stripe recurring interval
 * @param {number} count Stripe interval_count
 * @returns {string}
 */
export function translateCyclePeriod(
  i18n: BillingPeriodTranslator,
  interval: StripeRecurringInterval,
  count: number,
): string {
  const label = cyclePeriodLabel(interval, count);
  return i18n.translate(label.key, label.params);
}

/**
 * Translates the catalog period label for cards or invoice confirm.
 * @param {BillingPeriodTranslator} i18n Transloco service
 * @param {Pick<StripeProduct, 'recurring'> | null | undefined} product Catalog price
 * @param {StripeBillingInterval} billingInterval Selected billing toggle fallback
 * @returns {string}
 */
export function translateBillingPeriod(
  i18n: BillingPeriodTranslator,
  product: Pick<StripeProduct, 'recurring'> | null | undefined,
  billingInterval: StripeBillingInterval,
): string {
  const cycle = resolveBillingCycle(product, billingInterval);
  return translateCyclePeriod(i18n, cycle.interval, cycle.count);
}

/**
 * Formats a subscription summary price with its billing cycle label.
 * Always shows the full cycle amount (never monthlyized).
 * @param {BillingPeriodTranslator} i18n Transloco service
 * @param {number | null | undefined} costCents Recurring cost in cents
 * @param {StripeRecurringInterval | null | undefined} interval Billing interval
 * @param {number | null | undefined} [intervalCount] Stripe interval_count
 * @returns {string | null}
 */
export function formatSubscriptionPlanPrice(
  i18n: BillingPeriodTranslator,
  costCents: number | null | undefined,
  interval: StripeRecurringInterval | null | undefined,
  intervalCount?: number | null,
): string | null {
  if (costCents == null || costCents <= 0) {
    return null;
  }
  const amount = new CurrencyPipe(i18n.getActiveLang()).transform(
    costCents / 100,
    'EUR',
    'symbol',
    '1.0-0',
  );
  if (!amount) {
    return null;
  }
  if (!interval) {
    return amount;
  }
  const count =
    typeof intervalCount === 'number' && intervalCount > 0 ? intervalCount : 1;
  return i18n.translate(PRICE_WITH_PERIOD_KEY, {
    price: amount,
    period: translateCyclePeriod(i18n, interval, count),
  });
}
